import { createSlice } from '@reduxjs/toolkit';
import axios from '../../services/authService';
import { API_ENDPOINTS } from '../../config/apiConfig';

const initialState = {
  jobId: null,
  status: 'idle',
  result: null,
  error: null,
  starting: false,
  polling: false,
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.detail ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const eventFailureTicketsSlice = createSlice({
  name: 'eventFailureTickets',
  initialState,
  reducers: {
    startJobPending: (state) => {
      state.jobId = null;
      state.status = 'starting';
      state.result = null;
      state.error = null;
      state.starting = true;
      state.polling = false;
    },
    startJobFulfilled: (state, action) => {
      state.jobId = action.payload.job_id;
      state.status = action.payload.status || 'accepted';
      state.starting = false;
      state.polling = true;
    },
    startJobRejected: (state, action) => {
      state.status = 'failed';
      state.error = action.payload;
      state.starting = false;
      state.polling = false;
    },
    statusPending: (state) => {
      state.polling = true;
    },
    statusFulfilled: (state, action) => {
      const status = action.payload?.status || 'running';
      state.jobId = action.payload?.job_id || state.jobId;
      state.status = status;

      if (status === 'done') {
        state.result = action.payload?.result || null;
        state.error = null;
        state.polling = false;
      } else if (status === 'failed') {
        state.error = action.payload?.error || 'Event failure ticket job failed.';
        state.polling = false;
      } else {
        state.polling = true;
      }
    },
    statusRejected: (state, action) => {
      state.status = 'failed';
      state.error = action.payload;
      state.polling = false;
    },
    stopEventFailureTicketsPolling: (state) => {
      state.polling = false;
    },
  },
});

const {
  startJobPending,
  startJobFulfilled,
  startJobRejected,
  statusPending,
  statusFulfilled,
  statusRejected,
  stopEventFailureTicketsPolling,
} = eventFailureTicketsSlice.actions;

export { stopEventFailureTicketsPolling };

export const startEventFailureTicketsJobAsync = (payload) => async (dispatch) => {
  dispatch(startJobPending());

  try {
    const res = await axios.post(API_ENDPOINTS.CREATE_EVENT_FAILURE_TICKETS, payload);
    const data = res.data || {};

    if (!data.job_id) {
      throw new Error('Job ID missing from event failure ticket response.');
    }

    dispatch(startJobFulfilled(data));
    return { success: true, data };
  } catch (error) {
    const message = getErrorMessage(error, 'Failed to start event failure ticket job.');
    dispatch(startJobRejected(message));
    return { success: false, message };
  }
};

export const getEventFailureTicketsStatusAsync = (jobId) => async (dispatch) => {
  if (!jobId) {
    const message = 'Job ID is required to check event failure ticket status.';
    dispatch(statusRejected(message));
    return { success: false, message };
  }

  dispatch(statusPending());

  try {
    const res = await axios.get(API_ENDPOINTS.EVENT_FAILURE_TICKETS_STATUS(jobId));
    const data = res.data || {};
    dispatch(statusFulfilled(data));
    return { success: true, data };
  } catch (error) {
    const message = getErrorMessage(error, 'Failed to check event failure ticket status.');
    dispatch(statusRejected(message));
    return { success: false, message };
  }
};

export const selectEventFailureTicketsJobId = (state) => state.eventFailureTickets.jobId;
export const selectEventFailureTicketsStatus = (state) => state.eventFailureTickets.status;
export const selectEventFailureTicketsResult = (state) => state.eventFailureTickets.result;
export const selectEventFailureTicketsError = (state) => state.eventFailureTickets.error;
export const selectEventFailureTicketsStarting = (state) => state.eventFailureTickets.starting;
export const selectEventFailureTicketsPolling = (state) => state.eventFailureTickets.polling;

export default eventFailureTicketsSlice.reducer;
